import type { VideoPlayer } from "@zoom/videosdk";
import { SetStateAction } from "jotai";
import {
  ComponentType,
  CSSProperties,
  HTMLAttributes,
  MutableRefObject,
  ReactNode,
  RefObject,
} from "react";
import type {
  CreateLocalTrackOptions as TwilioCreateLocalTrackOptions,
  LocalAudioTrack as TwilioLocalAudioTrack,
  LocalAudioTrackStats as TwilioLocalAudioTrackStats,
  LocalDataTrack as TwilioLocalDataTrack,
  LocalVideoTrack as TwilioLocalVideoTrack,
  NoiseCancellation as TwilioNoiseCancellation,
} from "twilio-video";
import {
  LocalAudioDelegator,
  LocalScreenShareDelegator,
  LocalVideoDelegator,
  RemoteAudioDelegator,
  RemoteVideoDelegator,
} from "./room/participant/media/MediaDelegator";
import { QualityMode } from "./room/participant/media/types";
import { RemoteParticipant } from "./room/participant/RemoteParticipant";
import {
  ParticipantMediaStatusByParticipantId,
  RemoteAudioStatsByTrackSid,
  RemoteParticipantMediaByParticipantId,
  userDataByParticipantId,
} from "./room/participant/types";
import { RoomDelegator } from "./room/RoomDelegator";

export interface ConnectLocalAudioVideo {
  getLocalAudioContext: () => LocalAudioContextAtom;
  setLocalAudioContext: LocalAudioContextSetter;
  getLocalVideoContext: () => LocalVideoContextAtom;
  setLocalVideoContext: LocalVideoContextSetter;
  setHasBackgroundProcessorFailure: (value: boolean) => void;
  setStoredAudioVideoSettings: (
    updater: (prev: AudioVideoSettings) => AudioVideoSettings,
  ) => void;
  trackOptions?: Partial<TwilioCreateLocalTrackOptions>;
  disableSessionAudioVideo?: boolean;
}

export interface JoinRoomResult {
  room: RoomDelegator;
  localDataContext: LocalDataContextAtom;
}

export type LocalVideoComponentProps = {
  videoProps: HTMLAttributes<HTMLVideoElement>;
  videoRef: RefObject<HTMLVideoElement | VideoPlayer>;
};

export type RemoteAudioVideoComponentProps = {
  remoteParticipantId: string;
  audioRef: RefObject<HTMLAudioElement>;
  videoRef: RefObject<HTMLVideoElement | VideoPlayer>;
};

export type ContainerComponentProps = {
  dataTestId?: string;
  className?: string;
  styleOverride?: CSSProperties;
  children: ReactNode;
};

export type LocalScreenShareComponentProps = {
  videoRef: MutableRefObject<HTMLVideoElement | null>;
};

export type RemoteScreenShareComponentProps = {
  audioRef?: MutableRefObject<HTMLAudioElement | null>;
  videoRef: MutableRefObject<HTMLVideoElement | VideoPlayer | null>;
};

export type VideoComponents = {
  Container: ComponentType<ContainerComponentProps>;
  LocalVideo: ComponentType<LocalVideoComponentProps>;
  RemoteAudioVideo: ComponentType<RemoteAudioVideoComponentProps>;
  LocalScreenShare: ComponentType<LocalScreenShareComponentProps>;
  RemoteScreenShare: ComponentType<RemoteScreenShareComponentProps>;
};

// ================================================================================
// Migrated from ../twilio/config.ts; keep together as there's a circular dependency
// ================================================================================

export enum ThumbnailPosition {
  TOP_LEFT = "top-2 left-2",
  TOP_RIGHT = "top-2 right-2",
  BOTTOM_RIGHT = "bottom-2 right-2",
  BOTTOM_LEFT = "bottom-12 left-2",
}

export type LocalAVTrack =
  | TwilioLocalVideoTrack
  | (TwilioLocalAudioTrack & { noiseCancellation?: TwilioNoiseCancellation });

export interface LocalAVTrackContextAtom {
  track?: LocalAVTrack;
  status?: LocalMediaStatus;
}

export interface LocalAudioContextAtom {
  delegator?: LocalAudioDelegator;
  status?: LocalMediaStatus;
}

export interface LocalVideoContextAtom {
  delegator?: LocalVideoDelegator;
  status?: LocalMediaStatus;
}

export interface LocalDataContextAtom {
  track?: TwilioLocalDataTrack;
  status?: LocalMediaStatus;
}

export type LocalScreenShareContextAtom = {
  delegator?: LocalScreenShareDelegator;
  status?: LocalMediaStatus;
};

export type AvailableDevices = {
  audioInputDevices: MediaDeviceInfo[];
  videoInputDevices: MediaDeviceInfo[];
  audioOutputDevices: MediaDeviceInfo[];
};

export type VideoSDKAtom = {
  availableDevices: AvailableDevices | null;
  connectedAt?: number;
  currentCameraId?: string;
  currentMicrophoneId?: string;
  currentOutputDeviceId?: string;
  dominantSpeakerId?: string;
  hasBackgroundProcessorFailure: boolean;
  isAudioPlaybackRestricted?: boolean;
  isShowingScreenShareConfirmation: boolean;
  localAudioContext: LocalAudioContextAtom;
  localAudioContextStats: TwilioLocalAudioTrackStats | null;
  localDataContext: LocalDataContextAtom;
  localScreenShareContext: LocalScreenShareContextAtom;
  localVideoContext: LocalVideoContextAtom;
  participants: RemoteParticipant[];
  pictureInPictureParticipantId?: string;
  remoteAudioTrackStatsBySid: RemoteAudioStatsByTrackSid;
  remoteScreenShareAudioDelegator?: RemoteAudioDelegator;
  remoteScreenShareParticipant?: RemoteParticipant;
  remoteScreenShareVideoDelegator?: RemoteVideoDelegator;
  remoteMediaByParticipantId: RemoteParticipantMediaByParticipantId;
  room?: RoomDelegator;
  roomToken?: string;
  shouldOverrideSuggestedRoomVideoQuality: boolean;
  shouldTriggerPictureInPicture: boolean;
  thumbnailPosition: ThumbnailPosition;
  remoteMediaStatusByParticipantId: ParticipantMediaStatusByParticipantId;
  twilioIdentity?: string;
  usedTwilioWebsocketsSuccessfully?: boolean;
  userDataByParticipantId: userDataByParticipantId;
  usingWebsocketsReverseProxy?: boolean;
  videoQuality: QualityMode;
  videoWasOnAtScreenLeave: boolean | null;
  zoomToken?: string;
};

export type Constraint =
  | ConstrainDOMString
  | ConstrainBoolean
  | ConstrainDouble
  | ConstrainULong;

// Setter types for Jotai atoms that support both direct values and functional updates
export type LocalAudioContextSetter = (
  update: SetStateAction<LocalAudioContextAtom>,
) => void;

export type LocalVideoContextSetter = (
  update: SetStateAction<LocalVideoContextAtom>,
) => void;

export type LocalScreenShareContextSetter = (
  update: SetStateAction<LocalScreenShareContextAtom>,
) => void;

export type RecordingActionResult =
  | { success: true; error: null }
  | { success: false; error: Error };

export enum LocalMediaStatus {
  LOADING = "loading",
  READY = "ready",
  DISABLED = "disabled",
  ERROR = "error",
}

export enum BackgroundType {
  NONE = "none",
  BLUR = "blur",
  IMAGE = "image",
}

export type BackgroundSettings = {
  backgroundIndex?: number;
  backgroundType: BackgroundType;
};

export type AudioVideoSettings = {
  isAudioEnabled: boolean;
  isVideoEnabled: boolean;
  inputAudioDeviceId?: string;
  inputVideoDeviceId?: string;
  videoIsMirrored: boolean;
  videoQualityMode: QualityMode;
  selfViewIsHidden: boolean;
  noiseCancellationIsEnabled: boolean;
} & BackgroundSettings;

type BasePublishMediaOptions = {
  backgroundOptions?: BackgroundSettings;
  mediaStatus?: LocalMediaStatus;
};

/**
 * Publish media options. Conditionally includes vendor-specific properties.
 * - `PublishMediaOptions` - base options only
 * - `PublishMediaOptions<TTrackOptions>` - includes trackOptions
 * - `PublishMediaOptions<TTrackOptions, TTrack>` - includes trackOptions and track
 */
export type PublishMediaOptions<
  TTrackOptions = undefined,
  TTrack = undefined,
> = BasePublishMediaOptions &
  (TTrackOptions extends undefined
    ? Record<string, never>
    : { trackOptions?: TTrackOptions }) &
  (TTrack extends undefined ? Record<string, never> : { track?: TTrack });

export enum ChangeCameraStatusCode {
  "CHANGED" = "changed",
  "BACKGROUND_ERROR" = "background_error",
  "ERROR" = "error",
}

type BaseSetCameraResult = {
  success: boolean;
  code: ChangeCameraStatusCode;
  status?: LocalMediaStatus;
};

/**
 * Camera switch result. Optionally includes vendor-specific track.
 * - `SetCameraResult` - no track property (Zoom)
 * - `SetCameraResult<TTrack>` - includes optional track property (Twilio)
 */
export type SetCameraResult<TTrack = undefined> = TTrack extends undefined
  ? BaseSetCameraResult
  : BaseSetCameraResult & { track?: TTrack };
